import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { ArrowLeftIcon, ChevronDownIcon, ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/solid';
import { motion, AnimatePresence } from 'framer-motion';
import Avatar from "../components/Avatar";

export default function MyBetsPage() {
  const navigate = useNavigate();
  const [bets, setBets] = useState([]);
  const [filteredBets, setFilteredBets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [summary, setSummary] = useState({ won: 0, lost: 0, roi: 0 });
  const [expandedBet, setExpandedBet] = useState(null);

  useEffect(() => {
    const fetchUserBets = async () => {
      try {
        const response = await api.get('/bets/user');
        const allBets = response.data.bets || [];

        setBets(allBets);
        applyFilter(allBets, filter);

        const won = allBets
          .filter(b => b.result === "win")
          .reduce((sum, b) => sum + (b.amount * b.odds), 0);

        const lost = allBets
          .filter((b) => b.result === 'lose')
          .reduce((sum, b) => sum + b.amount, 0);

        // Calculate ROI
        const totalInvested = allBets.reduce((sum, b) => sum + b.amount, 0);
        const roi = totalInvested > 0 ? ((won - lost) / totalInvested * 100).toFixed(2) : 0;

        setSummary({ won, lost, roi });
      } catch (err) {
        console.error('❌ Failed to fetch bets:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserBets();
  }, []);

  const applyFilter = (all, filterType) => {
    let filtered = all;
    if (filterType === 'pending') filtered = all.filter((b) => b.status === 'pending');
    else if (filterType === 'won') filtered = all.filter((b) => b.result === 'win');
    else if (filterType === 'lost') filtered = all.filter((b) => b.result === 'lose');
    setFilteredBets(filtered);
  };

  const handleFilterChange = (type) => {
    setFilter(type);
    applyFilter(bets, type);
  };

  const formatDate = (date) =>
    new Date(date).toLocaleString('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });

  // Toggle expanded bet details
  const toggleExpandBet = (betId) => {
    if (expandedBet === betId) {
      setExpandedBet(null);
    } else {
      setExpandedBet(betId);
    }
  };

  // Loading state component
  const renderLoading = () => (
    <div className="flex items-center justify-center h-48 sm:h-64">
      <div className="flex flex-col items-center">
        <div className="w-12 h-12 sm:w-16 sm:h-16 border-2 border-violet-500 rounded-full relative flex items-center justify-center">
          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-violet-500 via-pink-500 to-red-500 rounded-full animate-pulse"></div>
          <div className="absolute inset-0 border-t-2 border-violet-500 rounded-full animate-spin"></div>
        </div>
        <div className="mt-4 font-mono text-sm">
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
            Loading Bets...
          </span>
        </div>
      </div>
    </div>
  );

  // Calculate the height for a single row (approximately)
  // Each row is about 53px tall when not expanded
  const rowHeight = 53; 
  // Max rows to show without scrolling
  const maxRowsToShow = 10;

  return (
    <main className="min-h-screen bg-gradient-to-br from-gray-900 to-black -mt-16 pt-4 pb-4 px-4 text-white font-mono">
      <div className="max-w-3xl mx-auto space-y-3 sm:space-y-4">
        {/* Header */}
        <div className="relative mb-8 flex justify-center items-center">
  {/* Back button positioned absolutely on the left */}
  <button
    onClick={() => navigate('/')}
    className="absolute left-0 flex items-center text-pink-400 hover:text-pink-300 transition"
  >
    <ArrowLeftIcon className="h-5 w-5" />
  </button>

  {/* Centered Heading */}
  <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-violet-500 via-pink-500 to-red-500">
    My Bets
  </h1>
</div>


        {/* Summary Cards */}
        <div className="bg-gray-900/90 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden">
          <div className="px-3 sm:px-6 py-4 border-b border-gray-700/50 bg-black/30">
            <div className="text-xs font-semibold uppercase text-gray-400 text-center">Performance Summary</div>
          </div>
          <div className="grid grid-cols-3 gap-2 p-4">
            <div className="flex flex-col items-center justify-center p-3 bg-black/20 rounded-lg">
              <div className="text-green-400 font-semibold text-xs">Won</div>
              <div className="text-lg font-bold">{Math.floor(summary.won)} CHIPS</div>
            </div>
            <div className="flex flex-col items-center justify-center p-3 bg-black/20 rounded-lg">
              <div className="text-red-400 font-semibold text-xs">Lost</div>
              <div className="text-lg font-bold">{Math.floor(summary.lost)} CHIPS</div>
            </div>
            <div className="flex flex-col items-center justify-center p-3 bg-black/20 rounded-lg">
              <div
                className={`font-semibold text-xs flex items-center ${
                  parseFloat(summary.roi) >= 0 ? 'text-green-400' : 'text-red-400'
                }`}
              >
                {parseFloat(summary.roi) >= 0 ? (
                  <ArrowUpIcon className="h-3 w-3 mr-1" />
                ) : (
                  <ArrowDownIcon className="h-3 w-3 mr-1" />
                )}
                <span>ROI</span>
              </div>
              <div
                className={`text-lg font-bold ${
                  parseFloat(summary.roi) >= 0 ? 'text-green-400' : 'text-red-400'
                }`}
              >
                {summary.roi}%
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 justify-center mb-4">
          {['all', 'pending', 'won', 'lost'].map((f) => (
            <button
              key={f}
              onClick={() => handleFilterChange(f)}
              className={`px-3 sm:px-4 py-1 rounded-full text-xs font-medium transition border ${
                filter === f
                  ? 'bg-gradient-to-r from-violet-500 to-pink-500 text-white border-pink-600'
                  : 'bg-black/40 border-gray-700 hover:border-pink-500/50'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {/* Bets Table */}
        <div className="bg-gray-900/90 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden mb-4">
          {/* Table Header */}
          <div className="px-3 sm:px-6 py-4 border-b border-gray-700/50 bg-black/30">
            <div className="grid grid-cols-12 gap-1 sm:gap-2">
              <div className="col-span-4 sm:col-span-3 text-xs font-semibold uppercase text-gray-400">Symbol</div>
              <div className="col-span-4 sm:col-span-3 text-xs font-semibold uppercase text-gray-400 text-center">Stake</div>
              <div className="hidden sm:block sm:col-span-3 text-xs font-semibold uppercase text-gray-400 text-center">Odds</div>
              <div className="col-span-4 sm:col-span-3 text-xs font-semibold uppercase text-gray-400 text-center">Status</div>
            </div>
          </div>

          {/* Table Body - Now with fixed height and scrolling */}
          {loading ? (
            renderLoading()
          ) : filteredBets.length === 0 ? (
            <div className="p-8 text-center text-gray-400">No bets found in this category.</div>
          ) : (
            <div className="overflow-y-auto scrollbar-thin scrollbar-thumb-violet-500 scrollbar-track-gray-800" 
                 style={{ 
                   maxHeight: `${rowHeight * maxRowsToShow}px`,
                   height: 'auto'
                 }}>
              {filteredBets.map((bet, index) => {
                const isExpanded = expandedBet === bet._id;
                
                return (
                  <React.Fragment key={bet._id}>
                    {/* Main Row */}
                    <div 
                      className={`border-b border-gray-800/50 transition-colors duration-200 hover:bg-violet-900/10 ${
                        index % 2 === 0 ? 'bg-black/20' : ''
                      }`}
                    >
                      <div 
                        className="px-3 sm:px-6 py-3 grid grid-cols-12 gap-1 sm:gap-2 items-center cursor-pointer relative"
                        onClick={() => toggleExpandBet(bet._id)}
                      >
                        {/* Symbol */}
                        <div className="col-span-4 sm:col-span-3 font-medium text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400 truncate">
                          {bet.symbol}
                        </div>
                        
                        {/* Stake */}
                        <div className="col-span-4 sm:col-span-3 text-center">
                          <span className="text-white text-xs sm:text-sm">{bet.amount}</span>
                        </div>
                        
                        {/* Odds - Hidden on mobile */}
                        <div className="hidden sm:block sm:col-span-3 text-center">
                          <span className="text-white text-sm">{bet.odds}x</span>
                        </div>
                        
                        {/* Status */}
                        <div className="col-span-4 sm:col-span-3 text-center">
                          <div className={`inline-flex px-2 py-0.5 sm:py-1 rounded-full text-xs ${
                            bet.result === 'win'
                              ? 'bg-green-900/30 text-green-400 border border-green-500/30'
                              : bet.result === 'lose'
                              ? 'bg-red-900/30 text-red-400 border border-red-500/30'
                              : 'bg-yellow-900/30 text-yellow-400 border border-yellow-500/30'
                          }`}>
                            {bet.status === 'pending' ? 'Pending' : bet.result?.toUpperCase()}
                          </div>
                        </div>

                        {/* Chevron indicator */}
                        <div className="absolute right-2 sm:right-4">
                          {isExpanded ? (
                            <ChevronDownIcon className="h-4 w-4 text-pink-400" />
                          ) : (
                            <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                          )}
                        </div>
                      </div>
                      
                      {/* Expandable detail row */}
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                            className="bg-gradient-to-r from-violet-900/20 to-transparent border-t border-violet-500/20"
                          >
                            <motion.div 
                              initial={{ y: -10 }}
                              animate={{ y: 0 }}
                              exit={{ y: -10 }}
                              className="px-3 sm:px-6 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4"
                            >
                              {/* Show odds on mobile in expanded view */}
                              <div className="block sm:hidden">
                                <div className="text-xs text-gray-400 mb-1">Odds</div>
                                <div className="text-sm">{bet.odds}x</div>
                              </div>
                              
                              <div>
                                <div className="text-xs text-gray-400 mb-1">Placed</div>
                                <div className="text-sm">{formatDate(bet.createdAt)}</div>
                              </div>
                              
                              <div>
                                <div className="text-xs text-gray-400 mb-1">
                                  {bet.result === 'win' ? 'Payout' : bet.result === 'lose' ? 'Loss' : 'Potential Payout'}
                                </div>
                                <div className={`text-sm font-bold ${
                                  bet.result === 'win' 
                                    ? 'text-green-400' 
                                    : bet.result === 'lose' 
                                    ? 'text-red-400' 
                                    : 'text-yellow-400'
                                }`}>
                                  {bet.result === 'win' 
                                    ? `${bet.payout} CHIPS` 
                                    : bet.result === 'lose' 
                                    ? `${bet.amount} CHIPS`
                                    : `${bet.amount * bet.odds} CHIPS`
                                  }
                                </div>
                              </div>
                              
                              {/* View details button */}
                              <div className="sm:col-span-2 flex justify-center mt-2">
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate('/bet-success', { state: { betId: bet._id } });
                                  }}
                                  className="w-full sm:w-auto px-4 py-2 sm:py-1 bg-gradient-to-r from-violet-500 to-pink-500 rounded-full text-xs font-medium text-white hover:opacity-90 transition"
                                >
                                  View Bet Details
                                </button>
                              </div>
                            </motion.div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Helper Text */}
        <div className="text-xs text-gray-400 italic text-center mt-2">
          track your wins, learn from your losses.
        </div>
      </div>
    </main>
  );
}
